/**
 * Startup / route skeletons.
 *
 * They intentionally reuse the SAME geometry as the real shell
 * (`--sidebar-w`, `--topbar-h`, PageContainer padding) so that swapping a
 * skeleton for real content causes no layout jump. Colors come from the theme
 * tokens, so light, dark, every accent and RTL are handled for free.
 *
 * Animation is a single subtle `animate-pulse` — no spinners, no shimmer.
 */

import { cn } from "@/lib/cn";

/** One neutral placeholder block. */
export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn("rounded-sm bg-surface2 animate-pulse", className)} aria-hidden />;
}

/**
 * Full-screen app skeleton: sidebar + topbar + content.
 * Shown while the session is being checked, i.e. before we know whether the
 * user is authenticated — so it must contain NO user data at all.
 */
export function AppStartupSkeleton() {
  return (
    <div className="flex h-screen overflow-hidden bg-bg" role="status" aria-busy="true">
      <span className="sr-only">Chargement…</span>

      {/* Sidebar */}
      <aside className="w-[var(--sidebar-w)] min-w-[var(--sidebar-w)] bg-bg2 border-e border-border flex-col hidden md:flex">
        <div className="flex items-center gap-2.5 px-4 h-[var(--topbar-h)] border-b border-border">
          <div className="h-8 w-8 rounded-lg bg-primary/25 animate-pulse" aria-hidden />
          <SkeletonBlock className="h-3.5 w-24" />
        </div>
        <div className="flex-1 px-2.5 py-3 space-y-1.5">
          {Array.from({ length: 7 }).map((_, i) => (
            <SkeletonBlock key={i} className="h-8 w-full" />
          ))}
        </div>
        <div className="p-2.5 border-t border-border">
          <SkeletonBlock className="h-9 w-full" />
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="flex items-center gap-3 px-5 h-[var(--topbar-h)] min-h-[var(--topbar-h)] bg-bg border-b border-border">
          <SkeletonBlock className="h-9 w-full max-w-[420px]" />
          <div className="ms-auto flex items-center gap-2">
            <SkeletonBlock className="h-8 w-8 rounded-full" />
            <SkeletonBlock className="h-8 w-24" />
          </div>
        </header>
        <main className="flex-1 overflow-hidden">
          <PageLoadingSkeleton />
        </main>
      </div>
    </div>
  );
}

/**
 * In-shell page skeleton: page header + KPI row + table block.
 * Used as the Suspense fallback while a lazily-loaded route chunk downloads.
 */
export function PageLoadingSkeleton() {
  return (
    <div className="px-4 py-5 sm:px-6 lg:px-9 lg:py-8" role="status" aria-busy="true">
      <span className="sr-only">Chargement…</span>
      <div className="mb-7 flex items-start justify-between gap-4">
        <div className="space-y-2">
          <SkeletonBlock className="h-7 w-56" />
          <SkeletonBlock className="h-3.5 w-40" />
        </div>
        <SkeletonBlock className="h-9 w-32" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBlock key={i} className="h-[92px] rounded-lg" />
        ))}
      </div>
      <TableSkeleton rows={6} />
    </div>
  );
}

/** Table placeholder — matches DataTable row height. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <SkeletonBlock className="h-11 w-full rounded-none" />
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-4 py-3.5">
            <SkeletonBlock className="h-4 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Dashboard placeholder — KPI row + two chart cards. */
export function DashboardSkeleton() {
  return (
    <div className="px-4 py-5 sm:px-6 lg:px-9 lg:py-8" role="status" aria-busy="true">
      <span className="sr-only">Chargement…</span>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBlock key={i} className="h-[92px] rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4">
        <SkeletonBlock className="h-[280px] rounded-lg" />
        <SkeletonBlock className="h-[280px] rounded-lg" />
      </div>
    </div>
  );
}
